import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Pencil, Trash2, X, Check, Search, AlertTriangle } from 'lucide-react';
import api from '../utils/api';
import { fmtDate, fmtCurrency } from '../utils/helpers';
import { patchRows } from './gridShared';
import BulkActionBar from './BulkActionBar';
import ConfirmModal from './ConfirmModal';
import { Modal, FormField } from './Table';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// Receipts are facts about money arriving — safe, open data: editable,
// deletable and bulk-editable, unlike issued invoices. Amount is per-receipt
// only; bulk edits touch date/mode/reference/notes.
const MODES = ['Cash', 'Cheque', 'NEFT', 'UPI', 'Other'];

export default function ReceiptsTab() {
  const { can } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState(null);          // receipt being edited
  const [confirm, setConfirm] = useState(null);    // receipt pending delete
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState('');
  const [bulkDate, setBulkDate] = useState('');
  const lastIdx = useRef(null);

  const load = () => api.get('/ledger/receipts')
    .then(r => setReceipts(r.data))
    .catch(() => toast.error('Could not load receipts'))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return receipts;
    return receipts.filter(r =>
      [r.receipt_number, r.client_name, r.invoice_number, r.reference_number, r.payment_mode]
        .some(v => String(v || '').toLowerCase().includes(q)));
  }, [receipts, search]);

  const toggle = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clickBox = (e, row, idx) => {
    e.stopPropagation();
    if (e.shiftKey && lastIdx.current !== null) {
      const [lo, hi] = [Math.min(lastIdx.current, idx), Math.max(lastIdx.current, idx)];
      const ids = filtered.slice(lo, hi + 1).map(r => r.id);
      setSelected(prev => { const n = new Set(prev); ids.forEach(i => n.add(i)); return n; });
    } else toggle(row.id);
    lastIdx.current = idx;
  };
  const toggleAll = () => setSelected(prev => prev.size === filtered.length ? new Set() : new Set(filtered.map(r => r.id)));

  const saveEdit = async () => {
    const amt = parseFloat(edit.amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error('Amount must be more than zero'); return; }
    if (!edit.receipt_date) { toast.error('Pick a date'); return; }
    try {
      const r = await api.put(`/ledger/receipts/${edit.id}`, {
        amount: amt, receipt_date: edit.receipt_date, payment_mode: edit.payment_mode,
        reference_number: edit.reference_number || null, notes: edit.notes || null,
      });
      setReceipts(prev => patchRows(prev, edit.id, r.data));
      setEdit(null);
      toast.success('Receipt updated — the invoice balance has been recalculated');
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not save'); }
  };

  const doDelete = async row => {
    try {
      await api.delete(`/ledger/receipts/${row.id}`);
      setReceipts(prev => prev.filter(r => r.id !== row.id));
      setConfirm(null);
      toast.success(`${row.receipt_number} deleted — the invoice balance has been recalculated`);
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not delete'); }
  };

  const bulkUpdate = async updates => {
    const ids = Array.from(selected);
    try {
      await api.post('/ledger/receipts/bulk-update', { ids, updates });
      setReceipts(prev => patchRows(prev, ids, updates));
      setSelected(new Set()); setBulkMode(''); setBulkDate('');
      toast.success(`${ids.length} receipt${ids.length !== 1 ? 's' : ''} updated`);
    } catch (e) { toast.error(e?.response?.data?.error || 'Bulk update failed'); }
  };

  if (loading) return <div className="text-center py-16 text-gray-400">Loading…</div>;

  return (
    <div className="card">
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search receipt, client, invoice, reference…"
            className="input-field pl-9 w-80" />
        </div>
        <span className="flex items-center text-xs text-gray-500">
          {filtered.length} receipt{filtered.length !== 1 ? 's' : ''} · {fmtCurrency(filtered.reduce((s, r) => s + (r.amount || 0), 0))}
        </span>
      </div>

      <BulkActionBar
        module="billing"
        count={selected.size}
        onClear={() => setSelected(new Set())}
        extra={({ ask }) => (
          <>
            <span className="flex items-center gap-1">
              <select value={bulkMode} onChange={e => setBulkMode(e.target.value)} className="input-field text-xs py-1 w-32">
                <option value="">Set mode…</option>
                {MODES.map(m => <option key={m}>{m}</option>)}
              </select>
              <button disabled={!bulkMode}
                onClick={() => ask(`Set mode to "${bulkMode}" on ${selected.size} receipt${selected.size !== 1 ? 's' : ''}?`, () => bulkUpdate({ payment_mode: bulkMode }))}
                className="text-xs px-2 py-1.5 rounded-lg bg-[#1e3a5f] text-white disabled:opacity-40">Apply</button>
            </span>
            <span className="flex items-center gap-1">
              <input type="date" value={bulkDate} onChange={e => setBulkDate(e.target.value)} className="input-field text-xs py-1 w-36" />
              <button disabled={!bulkDate}
                onClick={() => ask(`Set date to ${fmtDate(bulkDate)} on ${selected.size} receipt${selected.size !== 1 ? 's' : ''}?`, () => bulkUpdate({ receipt_date: bulkDate }))}
                className="text-xs px-2 py-1.5 rounded-lg bg-[#1e3a5f] text-white disabled:opacity-40">Apply</button>
            </span>
          </>
        )}
      />

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="table-header w-8">
                <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} className="rounded" />
              </th>
              {['Receipt #', 'Date', 'Client', 'Against invoice', 'Amount', 'Mode', 'Reference', ''].map(h => (
                <th key={h} className="table-header whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-gray-400">No receipts found.</td></tr>
            )}
            {filtered.map((r, idx) => (
              <tr key={r.id} className="group border-b border-gray-50 hover:bg-gray-50/50">
                <td className="table-cell w-8">
                  <input type="checkbox" checked={selected.has(r.id)} onChange={() => {}}
                    onClick={e => clickBox(e, r, idx)} onMouseDown={e => e.shiftKey && e.preventDefault()} className="rounded" />
                </td>
                <td className="table-cell font-mono text-xs font-semibold text-[#1e3a5f]">{r.receipt_number}</td>
                <td className="table-cell whitespace-nowrap">{fmtDate(r.receipt_date)}</td>
                <td className="table-cell">{r.client_name || <span className="text-gray-300">—</span>}</td>
                <td className="table-cell">
                  {r.invoice_number
                    ? <span className={`font-mono text-xs ${r.invoice_state === 'cancelled' ? 'line-through text-gray-400' : ''}`}>{r.invoice_number}</span>
                    : r.invoice_id
                      ? <span className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle size={11} /> invoice deleted</span>
                      : <span className="text-xs text-gray-400">on account</span>}
                </td>
                <td className="table-cell font-semibold whitespace-nowrap">{fmtCurrency(r.amount)}</td>
                <td className="table-cell text-xs">{r.payment_mode}</td>
                <td className="table-cell text-xs text-gray-500">{r.reference_number || <span className="text-gray-300">—</span>}</td>
                <td className="table-cell w-16">
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {can('billing', 'edit') && (
                      <button onClick={() => setEdit({ ...r, amount: r.amount, reference_number: r.reference_number || '', notes: r.notes || '' })}
                        className="text-gray-400 hover:text-blue-500" aria-label={`Edit ${r.receipt_number}`}><Pencil size={14} /></button>
                    )}
                    {can('billing', 'delete') && (
                      <button onClick={() => setConfirm(r)} className="text-gray-400 hover:text-red-500" aria-label={`Delete ${r.receipt_number}`}><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={`Edit ${edit.receipt_number}`} onClose={() => setEdit(null)} size="md">
          <div className="space-y-3">
            {edit.invoice_number && (
              <p className="text-xs text-gray-500">
                Against invoice <span className="font-mono">{edit.invoice_number}</span> — changing the amount recalculates that invoice's balance.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Amount received" required>
                <input type="number" step="0.01" value={edit.amount} onChange={e => setEdit(x => ({ ...x, amount: e.target.value }))} className="input-field" autoFocus />
              </FormField>
              <FormField label="Date received" required>
                <input type="date" value={edit.receipt_date} onChange={e => setEdit(x => ({ ...x, receipt_date: e.target.value }))} className="input-field" />
              </FormField>
              <FormField label="Mode">
                <select value={MODES.includes(edit.payment_mode) ? edit.payment_mode : 'Other'}
                  onChange={e => setEdit(x => ({ ...x, payment_mode: e.target.value }))} className="input-field">
                  {MODES.map(m => <option key={m}>{m}</option>)}
                </select>
              </FormField>
              <FormField label="Reference">
                <input value={edit.reference_number} onChange={e => setEdit(x => ({ ...x, reference_number: e.target.value }))} className="input-field" placeholder="Cheque / UTR no." />
              </FormField>
            </div>
            <FormField label="Notes">
              <input value={edit.notes} onChange={e => setEdit(x => ({ ...x, notes: e.target.value }))} className="input-field w-full" />
            </FormField>
            {!MODES.includes(edit.payment_mode) && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                This receipt was recorded as "{edit.payment_mode}". Saving will store it under the mode selected above.
              </p>
            )}
            <div className="flex gap-3 pt-1">
              <button onClick={saveEdit} className="btn-primary flex-1 flex items-center justify-center gap-1.5"><Check size={15} /> Save</button>
              <button onClick={() => setEdit(null)} className="btn-secondary flex-1 flex items-center justify-center gap-1.5"><X size={15} /> Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {confirm && (
        <ConfirmModal
          message={`Delete ${confirm.receipt_number} for ${fmtCurrency(confirm.amount)}${confirm.invoice_number ? ` against ${confirm.invoice_number}` : ''}? The invoice balance will go back up by that amount.`}
          onConfirm={() => doDelete(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
